import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/kb/$slug")({
  loader: async ({ params }) => {
    const { data, error } = await supabase
      .from("kb_articles")
      .select("id,title,slug,excerpt,content,category_id,updated_at,published")
      .eq("slug", params.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw notFound();

    let categoryName: string | null = null;
    if (data.category_id) {
      const { data: cat } = await supabase
        .from("kb_categories")
        .select("name")
        .eq("id", data.category_id)
        .maybeSingle();
      categoryName = cat?.name ?? null;
    }
    return { article: data, categoryName };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.article.title ?? "Article";
    const desc = loaderData?.article.excerpt ?? "Knowledge base article";
    return {
      meta: [
        { title: `${title} — Knowledge Base` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">Article not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        The article you're looking for doesn't exist or isn't published.
      </p>
      <Link to="/kb" className="mt-4 inline-block text-sm text-primary hover:underline">
        ← Back to knowledge base
      </Link>
    </div>
  ),
  component: KbArticlePage,
});

function KbArticlePage() {
  const { article, categoryName } = Route.useLoaderData();

  // Subscribe to fresh data on client (in case the article was updated)
  const { data } = useQuery({
    queryKey: ["kb_article", article.slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_articles")
        .select("id,title,slug,excerpt,content,category_id,updated_at")
        .eq("slug", article.slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    initialData: article,
  });

  const a = data ?? article;

  return (
    <article className="mx-auto max-w-3xl px-4 py-10 md:py-14">
      <Link to="/kb" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Knowledge Base
      </Link>

      <header className="mt-4 space-y-3">
        {categoryName && <Badge variant="outline">{categoryName}</Badge>}
        <h1 className="text-3xl font-semibold md:text-4xl">{a.title}</h1>
        {a.excerpt && <p className="text-base text-muted-foreground">{a.excerpt}</p>}
        <div className="text-xs text-muted-foreground">
          Updated {new Date(a.updated_at).toLocaleDateString()}
        </div>
      </header>

      <div className="prose prose-sm md:prose-base dark:prose-invert mt-8 max-w-none">
        <ReactMarkdown>{a.content || ""}</ReactMarkdown>
      </div>
    </article>
  );
}
